import React from "react";
import { CalculatorInput } from "./CalculatorInput";
import type { SliderFieldProps } from "./SipCalculatorTypes";
import { DEFAULT_SIP_CALCULATOR_SYMBOLS, resolveSliderInputAffix } from "./SipCalculatorConstants";
import {
  buildMarkerValues,
  evenMarkerPercentToValue,
  formatMarkerLabel,
  sanitizeCurrencyDigits,
  sanitizeTenureOrPercentDigits,
  valueToEvenMarkerPercent,
} from "./SipCalculatorHelpers";

export const SliderField: React.FC<SliderFieldProps> = ({
  label,
  kind,
  value,
  inputValue,
  min,
  max,
  step,
  error,
  markerValues,
  symbols = DEFAULT_SIP_CALCULATOR_SYMBOLS,
  onInputChange,
  mobileFocusHighlight = false,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isSliderActive, setIsSliderActive] = React.useState(false);
  const inputAffix = React.useMemo(() => resolveSliderInputAffix(kind, symbols), [kind, symbols]);
  const hasInputValue = inputValue.trim().length > 0;

  const parsedInputValue = React.useMemo(() => {
    if (kind === "currency") {
      let v = inputValue.replace(/,/g, "").toUpperCase();
      if (v.endsWith("K")) v = String(Number(v.replace("K", "")) * 1000);
      if (v.endsWith("L")) v = String(Number(v.replace("L", "")) * 100000);
      const n = Number(v);
      return Number.isFinite(n) ? n : value;
    }
    if (kind === "percent") {
      const n = Number(inputValue);
      return Number.isFinite(n) ? Math.round(n) : Math.round(value);
    }
    const n = Number(inputValue);
    return Number.isFinite(n) ? n : value;
  }, [inputValue, kind, value]);

  const isTenureZeroInput = kind === "years" && hasInputValue && Number(inputValue) === 0;
  const isEmptyOrZeroInput =
    !hasInputValue || isTenureZeroInput || (kind !== "years" && parsedInputValue === 0);

  // Extract nested ternary into independent statements for clarity
  let sliderValue: number;
  if (isEmptyOrZeroInput) {
    sliderValue = min;
  } else if (error) {
    sliderValue = value;
  } else {
    sliderValue = parsedInputValue;
  }

  const markers = React.useMemo(
    () => markerValues ?? buildMarkerValues(min, max),
    [markerValues, max, min]
  );

  const useEvenMarkerScale = markers.length >= 2;

  const sliderPercent = React.useMemo(() => {
    if (useEvenMarkerScale) {
      return valueToEvenMarkerPercent(sliderValue, markers, min, max);
    }
    return max === min ? 0 : ((sliderValue - min) / (max - min)) * 100;
  }, [useEvenMarkerScale, sliderValue, markers, min, max]);

  const sliderInternalValue = React.useMemo(
    () => Math.round((sliderPercent / 100) * 1000),
    [sliderPercent]
  );

  const handleSliderChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const position = Number(event.target.value);
      const percent = (position / 1000) * 100;

      if (useEvenMarkerScale) {
        const nextValue = evenMarkerPercentToValue(percent, markers, min, max, step);
        onInputChange(String(kind === "percent" ? Math.round(nextValue) : nextValue));
        return;
      }

      onInputChange(event.target.value);
    },
    [useEvenMarkerScale, markers, min, max, step, onInputChange, kind]
  );

  const isCurrency = kind === "currency";

  const placeholder = React.useMemo(() => {
    if (kind === "currency") return "Enter amount";
    if (kind === "years") return "Enter tenure";
    if (kind === "percent") return "Enter rate";
    return undefined;
  }, [kind]);

  const moveInputCursorToEnd = React.useCallback(() => {
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  }, []);

  const displayedValue = React.useMemo(() => {
    if (!hasInputValue) return "";
    if (kind === "currency") {
      // Show raw numeric value without commas in the input
      return inputValue.replace(/,/g, "");
    }
    return inputValue;
  }, [hasInputValue, inputValue, kind]);

  return (
    <div className="px-[8px] pt-[8px] pb-[8px] md:px-[20px] md:pt-[28px] md:pb-[24px]">
      <div className="flex items-center justify-between gap-[8px]">
        <div className="flex min-w-0 items-center">
          <p className="font-rubik text-black-normal text-[14px] leading-[18px] font-medium tracking-[0px] whitespace-nowrap md:text-[16px]">
            {label}
          </p>
        </div>

        <CalculatorInput
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={displayedValue}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const rawValue = event.target.value.replace(/,/g, "");
            if (isCurrency) {
              const { value, rejected, moveCursorToEnd } = sanitizeCurrencyDigits(
                inputValue,
                rawValue
              );
              if (rejected) {
                moveInputCursorToEnd();
                return;
              }
              onInputChange(value);
              if (moveCursorToEnd) {
                moveInputCursorToEnd();
              }
              return;
            }
            if (kind === "years" || kind === "percent") {
              const { value, moveCursorToEnd } = sanitizeTenureOrPercentDigits(rawValue);
              onInputChange(value);
              if (moveCursorToEnd) {
                moveInputCursorToEnd();
              }
              return;
            }
            onInputChange(rawValue);
          }}
          aria-label={label}
          placeholder={placeholder}
          contentAlign={isCurrency ? "space-between" : "end"}
          prefix={isCurrency && hasInputValue ? inputAffix.prefix : undefined}
          suffix={!isCurrency && hasInputValue ? inputAffix.suffix : undefined}
          error={Boolean(error)}
          mobileFocusHighlight={mobileFocusHighlight}
          showFocusStyle={!isSliderActive}
          wrapperClassName="h-[44px] w-auto max-w-[150px] px-[12px] py-[8px]"
          inputClassName="font-rubik text-[14px] font-medium leading-[1.25] text-black-normal md:text-[16px]"
          prefixClassName="font-rubik text-[14px] font-medium leading-none md:text-[16px]"
          suffixClassName="font-rubik text-[14px] font-medium leading-none md:text-[16px]"
        />
      </div>

      <div className="mt-[12px]">
        <input
          type="range"
          min={useEvenMarkerScale ? 0 : min}
          max={useEvenMarkerScale ? 1000 : max}
          step={useEvenMarkerScale ? 1 : step}
          value={sliderInternalValue}
          onChange={handleSliderChange}
          onPointerDown={() => {
            setIsSliderActive(true);
            inputRef.current?.blur();
          }}
          onPointerUp={() => setIsSliderActive(false)}
          onPointerCancel={() => setIsSliderActive(false)}
          onLostPointerCapture={() => setIsSliderActive(false)}
          onBlur={() => setIsSliderActive(false)}
          aria-label={`${label} slider`}
          style={{
            background: `linear-gradient(to right, #5164C8 0%, #5164C8 ${sliderPercent}%, #F2F2F2 ${sliderPercent}%, #F2F2F2 100%)`,
          }}
          className="h-[4px] w-full appearance-none rounded-full shadow-[0px_1px_1px_0px_#00000040_inset] md:h-[8px] [&::-moz-range-thumb]:h-[14px] [&::-moz-range-thumb]:w-[20px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[1.55px] [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-[linear-gradient(180deg,#979CDC_0%,#384791_100%)] [&::-moz-range-thumb]:shadow-[0px_0px_7.7px_0px_rgba(0,0,0,0.4)] md:[&::-moz-range-thumb]:h-[24px] md:[&::-moz-range-thumb]:w-[34px] md:[&::-moz-range-thumb]:border-[3px] [&::-webkit-slider-runnable-track]:h-[4px] [&::-webkit-slider-runnable-track]:rounded-full md:[&::-webkit-slider-runnable-track]:h-[8px] [&::-webkit-slider-thumb]:mt-[-5px] [&::-webkit-slider-thumb]:h-[14px] [&::-webkit-slider-thumb]:w-[20px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[1.55px] [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-[linear-gradient(180deg,#979CDC_0%,#384791_100%)] [&::-webkit-slider-thumb]:shadow-[0px_0px_7.7px_0px_rgba(0,0,0,0.4)] md:[&::-webkit-slider-thumb]:mt-[-8px] md:[&::-webkit-slider-thumb]:h-[24px] md:[&::-webkit-slider-thumb]:w-[34px] md:[&::-webkit-slider-thumb]:border-[3px]"
        />

        <div className="mt-[8.9px] flex items-center justify-between pr-[7px] pl-[7px] md:mt-[8.9px]">
          {markers.map((marker, index) => {
            const roundedMarker = kind === "currency" ? marker : Math.round(marker);
            const roundedValue = kind === "currency" ? sliderValue : Math.round(sliderValue);
            const isSelected = roundedMarker === roundedValue;

            return (
              <React.Fragment key={`${label}-${marker}-${index}`}>
                {index > 0 && markers.length > 2 ? (
                  <span className="font-rubik text-grey-medium text-center text-[8px] leading-none font-normal md:text-[14px]">
                    |
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() =>
                    onInputChange(String(kind === "currency" ? marker : Math.round(marker)))
                  }
                  className={`font-rubik m-0 cursor-pointer border-0 bg-transparent p-0 leading-none select-none ${isSelected ? "text-black-normal text-[8px] font-medium md:text-[14px]" : "text-grey-medium text-[8px] font-normal md:text-[14px]"}`}
                >
                  {formatMarkerLabel(marker, kind)}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        {error ? (
          <p className="font-rubik text-red-dark mt-1 flex items-center gap-1 text-[12px] leading-4">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="shrink-0"
              aria-hidden="true"
            >
              <circle cx="7" cy="7" r="7" fill="#B40000" />
              <path
                d="M7 3.5V7.5M7 9.5V10.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
};
